import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plane, Clock, MapPin, Search, Bell, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useGateChanges } from "@/hooks/useGateChanges";
import { SignupNudge } from "./SignupNudge";

interface Flight {
  flight_number: string;
  airline: string;
  destination: string;
  destination_code: string;
  gate: string | null;
  departure_time: string;
  status: string;
  status_color: string;
}

interface FlightPreviewProps {
  airportCode: string;
  airportName: string;
}

export const FlightPreview = ({ airportCode, airportName }: FlightPreviewProps) => {
  const { user } = useAuth();
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const { getGateChangeForFlight } = useGateChanges(airportCode);

  useEffect(() => {
    const fetchFlights = async () => {
      try {
        setLoading(true);
        const { data, error: fnError } = await supabase.functions.invoke("get-flights", {
          body: { airport: airportCode, terminal: "A" },
        });
        if (fnError) throw fnError;
        if (data?.flights) {
          setFlights(data.flights);
        }
      } catch (err) {
        console.error("Error fetching flights:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchFlights();
  }, [airportCode]);

  const formatTime = (timestamp: string) =>
    new Date(timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });

  const formatRelativeTime = (timestamp: string) => {
    const mins = Math.round((Date.now() - new Date(timestamp).getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    return `${Math.round(mins / 60)}h ago`;
  };

  // Don't show a broken card: hide the section entirely when there's nothing to show.
  if (!loading && (error || flights.length === 0)) return null;

  const filtered = search.trim()
    ? flights.filter(
        (f) =>
          f.flight_number.toLowerCase().includes(search.toLowerCase()) ||
          f.destination.toLowerCase().includes(search.toLowerCase()) ||
          f.airline?.toLowerCase().includes(search.toLowerCase())
      )
    : flights;

  return (
    <Card className="shadow-md">
      <CardContent className="pt-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-primary" aria-hidden="true" />
            <h3 className="text-lg font-semibold">Flights at {airportCode}</h3>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by flight, destination, or airline..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            aria-label="Search flights"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-3 rounded-lg border border-border animate-pulse">
                <div className="flex justify-between mb-2">
                  <div className="h-4 w-20 rounded bg-muted skeleton-shimmer" />
                  <div className="h-5 w-16 rounded-full bg-muted skeleton-shimmer" />
                </div>
                <div className="h-3 w-32 rounded bg-muted skeleton-shimmer" />
              </div>
            ))}
          </div>
        ) : error || flights.length === 0 ? (
          <p className="text-center py-6 text-sm text-muted-foreground">
            {error ? "Unable to load flight data right now." : "No flight data available."}
          </p>
        ) : (
          <>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filtered.slice(0, 8).map((flight, i) => {
                const gateChange = getGateChangeForFlight(flight.flight_number);

                return (
                  <div
                    key={i}
                    className={`p-3 rounded-lg border transition-colors ${
                      gateChange
                        ? "border-warning/50 bg-warning/5"
                        : "border-border hover:border-primary/30"
                    }`}
                  >
                    {/* Gate change alert */}
                    {gateChange && (
                      <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-warning">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        <span>
                          Gate changed: {gateChange.old_gate} → {gateChange.new_gate}
                          <span className="text-muted-foreground font-normal ml-1">
                            ({formatRelativeTime(gateChange.detected_at)})
                          </span>
                        </span>
                      </div>
                    )}

                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-sm">{flight.flight_number}</p>
                        <p className="text-xs text-muted-foreground">
                          {flight.airline} → {flight.destination}
                          {flight.destination_code && ` (${flight.destination_code})`}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          flight.status_color === "success"
                            ? "bg-success/10 text-success border-success/20"
                            : flight.status_color === "warning"
                            ? "bg-warning/10 text-warning border-warning/20"
                            : "bg-primary/10 text-primary border-primary/20"
                        }
                      >
                        {flight.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      {flight.gate && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> Gate {flight.gate}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {formatTime(flight.departure_time)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Gated feature nudge */}
            {!user && (
              <SignupNudge
                icon={<Bell className="h-4 w-4" />}
                message="Sign up to save flights and get gate change alerts"
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
